/**
 * Google (ENG-6165) and Microsoft (ENG-6169) sign-in through the api and
 * Cognito custom auth.
 *
 * 1. `startSocialSignIn`: the api verifies the provider's ID token and answers
 *    which account it signs in as, or that the page must confirm an existing
 *    account or sign up.
 * 2. `completeSocialSignIn`: an Amplify CUSTOM_WITHOUT_SRP sign-in for that
 *    username. The Create Auth Challenge trigger returns a nonce (Amplify's
 *    `additionalInfo`), the api signs a ticket bound to it, and the ticket
 *    answers the challenge. Cognito then issues its normal tokens.
 *
 * The provider's ID token and the ticket are credentials: they go straight to the
 * api and Amplify, and nothing here keeps or logs them.
 */
import {
  type SignInOutput,
  confirmSignIn as amplifyConfirmSignIn,
  signIn as amplifySignIn,
  signOut,
} from 'aws-amplify/auth';

import { gengql } from '@/__generated__';
import { SocialConnectProof, SocialProvider, SocialSignInStatus } from '@/__generated__/graphql';
import config from '@/config';
import type { GraphQLClient } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { isFeatureOn } from '@/core/growthbook';

import {
  activateCognitoSession,
  ensureCognitoConfigured,
  isCognitoLoginEnabled,
  resetCognitoSessionLocally,
} from './cognitoSession';
import { CognitoSignInError } from './cognitoSignIn';

export { SocialProvider };

export const GOOGLE_FLAG = 'social-google';
export const MICROSOFT_FLAG = 'social-microsoft';

const PROVIDER_NAMES: Record<SocialProvider, string> = {
  [SocialProvider.Google]: 'Google',
  [SocialProvider.Microsoft]: 'Microsoft',
};

/** The provider's name as the user knows it, for copy. */
export function socialProviderName(provider: SocialProvider): string {
  return PROVIDER_NAMES[provider];
}

/**
 * ENG-6192: how the one account the email matched is connected. NONE and
 * EMAIL_CODE carry the token socialSignIn issued for socialConnectSendCode and
 * socialConnectVerify; PASSWORD means a password sign-in first, then connect.
 */
export type SocialConnect =
  | { proof: 'NONE' | 'EMAIL_CODE'; token: string }
  | { proof: 'PASSWORD'; token?: undefined };

export type SocialStart =
  | { status: 'SIGN_IN'; username: string }
  | { status: 'CONFIRM_ACCOUNT'; email?: string; connect?: SocialConnect }
  | { status: 'SIGN_UP'; signupToken: string; email: string; firstName: string; lastName: string };

/** A provider's ID token and the pool username the api chose for it. */
export interface SocialCredential {
  provider: SocialProvider;
  username: string;
  idToken: string;
}

const SOCIAL_SIGN_IN = gengql(/* GraphQL */ `
  mutation authSocialSignIn($provider: SocialProvider!, $idToken: String!) {
    socialSignIn(provider: $provider, idToken: $idToken) {
      status
      username
      email
      signupToken
      firstName
      lastName
      connectProof
      connectToken
    }
  }
`);

const SOCIAL_TICKET = gengql(/* GraphQL */ `
  mutation authSocialTicket($provider: SocialProvider!, $idToken: String!, $nonce: String!) {
    socialTicket(provider: $provider, idToken: $idToken, nonce: $nonce)
  }
`);

const CONNECT_SOCIAL_IDENTITY = gengql(/* GraphQL */ `
  mutation authConnectSocialIdentity($provider: SocialProvider!, $idToken: String!) {
    connectSocialIdentity(provider: $provider, idToken: $idToken)
  }
`);

const DISCONNECT_SOCIAL_IDENTITY = gengql(/* GraphQL */ `
  mutation authDisconnectSocialIdentity($provider: SocialProvider!) {
    disconnectSocialIdentity(provider: $provider)
  }
`);

// `user` without an id is the viewer, the only reader of these two fields.
const SOCIAL_IDENTITIES = gengql(/* GraphQL */ `
  query authSocialIdentities {
    user {
      id
      has_password
      social_identities {
        provider
        email
      }
    }
  }
`);

const SOCIAL_CONNECT_SEND_CODE = gengql(/* GraphQL */ `
  mutation authSocialConnectSendCode($token: String!) {
    socialConnectSendCode(token: $token)
  }
`);

const SOCIAL_CONNECT_VERIFY = gengql(/* GraphQL */ `
  mutation authSocialConnectVerify($token: String!, $code: String) {
    socialConnectVerify(token: $token, code: $code)
  }
`);

const THROTTLED = new Set([
  'TooManyRequestsException',
  'LimitExceededException',
  'TooManyFailedAttemptsException',
]);

// Cognito refusing the custom flow: the user is unknown or disabled, or a
// trigger failed or rejected the ticket.
const REFUSED = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'UserLambdaValidationException',
  'InvalidLambdaResponseException',
  'UnexpectedLambdaException',
]);

/** "Continue with Google" is offered: Cognito login, the flag and a client id. */
export function isGoogleSignInEnabled(): boolean {
  return isCognitoLoginEnabled() && isFeatureOn(GOOGLE_FLAG) && !!config.googleClientId;
}

/** "Continue with Microsoft" is offered: Cognito login, the flag and a client id. */
export function isMicrosoftSignInEnabled(): boolean {
  return isCognitoLoginEnabled() && isFeatureOn(MICROSOFT_FLAG) && !!config.microsoftClientId;
}

function toConnect(
  proof: SocialConnectProof | null | undefined,
  token: string | null | undefined
): SocialConnect | undefined {
  switch (proof) {
    case undefined:
    case null:
      return undefined;
    case SocialConnectProof.Password:
      return { proof: 'PASSWORD' };
    case SocialConnectProof.None:
    case SocialConnectProof.EmailCode:
      if (!token) throw new Error(`socialSignIn answered proof ${proof} without a connect token`);
      return { proof, token };
    default:
      throw new Error(`unknown socialSignIn connect proof ${String(proof)}`);
  }
}

/** Ask the api which account a provider's ID token signs in as. */
export async function startSocialSignIn(
  client: GraphQLClient,
  provider: SocialProvider,
  idToken: string
): Promise<SocialStart> {
  const { socialSignIn: result } = await client.mutate(SOCIAL_SIGN_IN, { provider, idToken });
  switch (result.status) {
    case SocialSignInStatus.SignIn:
      if (!result.username) throw new Error('socialSignIn answered SIGN_IN without a username');
      return { status: 'SIGN_IN', username: result.username };
    case SocialSignInStatus.ConfirmAccount: {
      const connect = toConnect(result.connectProof, result.connectToken);
      return {
        status: 'CONFIRM_ACCOUNT',
        ...(result.email && { email: result.email }),
        ...(connect && { connect }),
      };
    }
    case SocialSignInStatus.SignUp:
      if (!result.signupToken || !result.email) {
        throw new Error('socialSignIn answered SIGN_UP without a signup token or email');
      }
      return {
        status: 'SIGN_UP',
        signupToken: result.signupToken,
        email: result.email,
        firstName: result.firstName ?? '',
        lastName: result.lastName ?? '',
      };
    default:
      throw new Error(`unknown socialSignIn status ${String(result.status)}`);
  }
}

function signInFailed(provider: SocialProvider, cause?: unknown): CognitoSignInError {
  return new CognitoSignInError(
    ERROR_CODES.AUTH_SOCIAL_TOKEN_INVALID,
    `We couldn't sign you in with ${socialProviderName(provider)}`,
    cause
  );
}

/** Amplify's errors to webapp codes; anything else (network, api) unchanged. */
function mapError(provider: SocialProvider, err: unknown): unknown {
  const name = err instanceof Error ? err.name : undefined;
  if (name && THROTTLED.has(name)) {
    return new CognitoSignInError(
      ERROR_CODES.AUTH_TOO_MANY_ATTEMPTS,
      'Too many sign-in attempts',
      err
    );
  }
  if (name && REFUSED.has(name)) return signInFailed(provider, err);
  return err;
}

async function startCustomAuth(username: string): Promise<SignInOutput> {
  const attempt = () =>
    amplifySignIn({ username, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
  try {
    return await attempt();
  } catch (err) {
    if (!(err instanceof Error && err.name === 'UserAlreadyAuthenticatedException')) throw err;
  }
  await signOut().catch(() => undefined);
  return attempt();
}

async function answerWithTicket(
  client: GraphQLClient,
  { provider, username, idToken }: SocialCredential
): Promise<void> {
  const { nextStep } = await startCustomAuth(username);
  const nonce =
    nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
      ? nextStep.additionalInfo?.nonce
      : undefined;
  if (!nonce) throw signInFailed(provider);

  const { socialTicket } = await client.mutate(SOCIAL_TICKET, { provider, idToken, nonce });
  const answered = await amplifyConfirmSignIn({ challengeResponse: socialTicket });
  // Anything but DONE (an MFA step, or the custom challenge again after a
  // rejected ticket) is a sign-in this flow cannot finish.
  if (answered.nextStep.signInStep !== 'DONE') throw signInFailed(provider);
}

/**
 * Sign in to Cognito as `username` (from `startSocialSignIn`) with a ticket
 * the api signs for this provider ID token. On any failure up to Cognito's DONE
 * the half-finished sign-in is signed out locally and the error thrown: a
 * `CognitoSignInError` for a Cognito refusal or an unexpected step, the api's
 * error unchanged. A failure while activating the session after DONE is
 * thrown without a sign-out, as in the password flow.
 */
export async function completeSocialSignIn(
  client: GraphQLClient,
  credential: SocialCredential
): Promise<void> {
  if (!ensureCognitoConfigured()) {
    throw new Error('Social sign-in called while Cognito login is off');
  }
  // As for a password sign-in: drop a live session locally first, so nothing
  // expires it while the new one is being established.
  resetCognitoSessionLocally();
  try {
    await answerWithTicket(client, credential);
  } catch (err) {
    await signOut().catch(() => undefined);
    throw mapError(credential.provider, err);
  }
  await activateCognitoSession();
}

/** Connect a provider account to the signed-in user. */
export async function connectSocialIdentity(
  client: GraphQLClient,
  provider: SocialProvider,
  idToken: string
): Promise<void> {
  await client.mutate(CONNECT_SOCIAL_IDENTITY, { provider, idToken });
}

/** Disconnect a provider account from the signed-in user. */
export async function disconnectSocialIdentity(
  client: GraphQLClient,
  provider: SocialProvider
): Promise<void> {
  await client.mutate(DISCONNECT_SOCIAL_IDENTITY, { provider });
}

export interface SocialIdentity {
  provider: SocialProvider;
  email: string;
}

/** The signed-in user's ways in besides email codes: a password, provider accounts. */
export interface SignInMethods {
  hasPassword: boolean;
  identities: SocialIdentity[];
}

/**
 * Read the signed-in user's connected provider accounts and whether they have
 * a password. An unknown `has_password` reads as none, so the last way in is
 * never offered for disconnection.
 */
export async function fetchSignInMethods(client: GraphQLClient): Promise<SignInMethods> {
  const { user } = await client.query(SOCIAL_IDENTITIES, undefined, {
    fetchPolicy: 'network-only',
  });
  return {
    hasPassword: user.has_password === true,
    identities: user.social_identities.map(({ provider, email }) => ({ provider, email })),
  };
}

/** Mail the code an EMAIL_CODE connect token asks for. */
export async function sendSocialConnectCode(client: GraphQLClient, token: string): Promise<void> {
  await client.mutate(SOCIAL_CONNECT_SEND_CODE, { token });
}

/**
 * Connect the provider account to the account socialSignIn offered, with the
 * mailed code for EMAIL_CODE, and answer the username to sign in as.
 */
export async function verifySocialConnect(
  client: GraphQLClient,
  token: string,
  code?: string
): Promise<string> {
  const { socialConnectVerify } = await client.mutate(SOCIAL_CONNECT_VERIFY, { token, code });
  return socialConnectVerify;
}
